<template>
	<div
		class="matchday--item"
		:class="{
			'is-active': event.isActive,
			'is-mobile': isMobileWebApp
		}"
		@click="handlePlay()"
	>
		<div>
			<div class="u-mb-3">
				<BadgeLive v-if="event.isLive" />
			</div>
			<template v-if="isMatch">
				<div class="matchday--item-infotext">
					{{ infoPreTitle }} • {{ infoSubTitle }}
				</div>
				<div
					class="matchday--item-stats"
					:class="[`-repeat-${periods.length}`]"
				>
					<div
						class="team-logo matchday--item-stats-item"
					>
						<img :src="teamHomeImage">
					</div>
					<div class="team-name matchday--item-stats-item">
						{{ event.teamHome ? event.teamHome.club.code : '' }}
					</div>
					<div class="score matchday--item-stats-item">
						{{ homeTeamScore }}
					</div>
					<div
						v-for="(period, i) in periods"
						:key="`setshome-${period.id}`"
						class="set matchday--item-stats-item"
						:class="{
							'-loser': ((period.teamHomeScoreInPeriod < period.teamGuestScoreInPeriod) && (i < periods.length-1 || !event.isLive)),
						}"
					>
						{{ period.teamHomeScoreInPeriod || '0' }}
					</div>
					<div
						class="team-logo matchday--item-stats-item"
					>
						<img :src="teamGuestImage">
					</div>
					<div class="team-name matchday--item-stats-item">
						{{ event.teamGuest ? event.teamGuest.club.code : '' }}
					</div>
					<div
						class="score matchday--item-stats-item -with-extra"
						:data-extra="scoreExtraAbbr"
					>
						{{ guestTeamScore }}
					</div>
					<div
						v-for="(period, i) in periods"
						:key="`setsguest-${period.id}`"
						class="set matchday--item-stats-item"
						:class="{'-loser': ((period.teamHomeScoreInPeriod > period.teamGuestScoreInPeriod) && (i < periods.length-1 || !event.isLive))}"
					>
						{{ period.teamGuestScoreInPeriod || '0' }}
					</div>
				</div>
			</template>
			<template v-else>
				<div class="h-disp-flex is-not-match">
					<div class="matchday--item-info">
						{{ infoPreTitle }}
					</div>
					<div class="event-name">
						{{ infoTitle }}
					</div>
					<div class="matchday--item-info">
						{{ infoSubTitle }}
					</div>
				</div>
			</template>
			<div
				v-if="event.league"
				class="matchday--status"
			>
				<img
					v-if="eventLeagueImg"
					:src="eventLeagueImg"
					class="event-league-image"
				>
				{{ leagueName }}
			</div>
		</div>
		<div class="divider" />
	</div>
</template>

<script>
import BadgeLive from '@shared/components/BadgeLive';
import {
	dateDayMonthnameYear,
	dateHoursMinutes,
} from '@videotools/js/util/timeFormat';
import { getEventStatus, EVENT_STATUS, getLocation, getCategoryParent, getFormattedTitle, getCategoryChild, getMatchScores } from '@shared/utils/eventUtils';
import { getDateTime, getRelativeTime } from '@shared/utils/timeUtils';
import { EVENT_TYPE, getEventType, getTags, isLeagueMatch } from '@shared/components/EventCard/EventCardProps';
import { mapState, mapGetters } from 'vuex';

export default {
	name:       'MatchdayListItem',
	components: { BadgeLive },
	props:      {
		event: {
			type:     [Object, null],
			required: true,
		},
		eventLeague: {
			type:     [Object, null],
			required: true,
			default:  () => {},
		},
	},
	data() {
		return {
			unixNow: Math.round(new Date().getTime() / 1000),
		};
	},
	computed: {
		...mapState({
			preferences: state => state.sharedUser.preferences,
		}),
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode || false;
		},
		score() {
			return this.event.finalScore;
		},
		homeTeamScore() {
			if (this.noSpoilerMode) return '-';
			if (this.score) {
				return this.score.teamHomeScore;
			// temp removal, see ASP-1127
			// } else if (this.event.isLive) {
			// 	return '0';
			}
			return '-';
		},
		guestTeamScore() {
			if (this.noSpoilerMode) return '-';
			if (this.score) {
				return this.score.teamGuestScore;
			// temp removal, see ASP-1127
			// } else if (this.event.isLive) {
			// 	return '0';
			}
			return '-';
		},
		scoreExtraAbbr() {
			if (this.noSpoilerMode) return null;
			if (this.score?.shootout) {
				return this.$I18n.trans('commons.shootout_abbr');
			}
			if (this.score?.overtime) {
				return this.$I18n.trans('commons.overtime_abbr');
			}
			return null;
		},
		periods() {
			// Note: As a first step, we want to show sets only for volleyball events. event.scores is an array with the sets. -> getMatchScores returns scores only for volleyball
			return getMatchScores(this.event).scores;
		},
		customDate() {
			const startDate = new Date(this.event.start);
			return dateDayMonthnameYear(startDate);
		},
		customTime() {
			const startDate = new Date(this.event.start);
			return dateHoursMinutes(startDate);
		},
		eventLeagueImg() {
			const img = this.eventLeague?.imageUrl;

			if (!img || img.includes('background_auth')) {
				return null;
			}

			return img;
		},
		leagueName() {
			return this.eventLeague ? this.eventLeague.name : '';
		},
		teamHomeImage() {
			if (!this.event.teamHome) {
				return null;
			}
			return this.$Helpers
				? this.$Helpers.getScaledImageUrl(this.event.teamHome.imageUrl, 50)
				: this.event.teamHome.imageUrl;
		},
		teamGuestImage() {
			if (!this.event.teamGuest) {
				return null;
			}
			return this.$Helpers && this.event.teamGuest.imageUrl
				? this.$Helpers.getScaledImageUrl(this.event.teamGuest.imageUrl, 50)
				: this.event.teamGuest.imageUrl;
		},
		location() {
		  return getLocation(this.event);
		},
		status() {
			return getEventStatus(this.event.type, this.event.startTimestamp, this.event.endTimestamp, this.unixNow);
		},
		infoPreTitle() {
			let infoPreTitle = '';
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			const gameStartDate = new Date(this.event.startTimestamp * 1000);
			const gameIsToday = gameStartDate.toLocaleDateString() === new Date().toLocaleDateString();
			const gameIsTomorrow = gameStartDate.toLocaleDateString() === tomorrow.toLocaleDateString();

			switch (this.status) {
			case EVENT_STATUS.UPCOMING:
				if (gameIsTomorrow) {
					infoPreTitle += `${this.$I18n.trans('commons.tomorrow')} · ${getDateTime(this.event.startTimestamp, { withDate: false })}`;
				} else if (gameIsToday) {
					infoPreTitle += `${this.$I18n.trans('commons.today')} · ${getDateTime(this.event.startTimestamp, { withDate: false })}`;
				} else {
					infoPreTitle += `${getDateTime(this.event.startTimestamp, { withWeekdayPrefix: true, dateTimeSeparator: ' · ' })}`;
				}
				break;
			case EVENT_STATUS.LIVE:
				infoPreTitle += getRelativeTime(this.event.endTimestamp, { useRemaining: true, unixNow: this.unixNow });
				break;
			default:
				infoPreTitle += getDateTime(this.event.startTimestamp, { withWeekdayPrefix: true, withTime: false });
			}

			if (!this.isLeagueMatch && this.eventType !== EVENT_TYPE.MATCH_OTHER && this.location) {
				infoPreTitle = `${infoPreTitle} · ${this.location}`;
			}

			return infoPreTitle;
		},
		infoTitle() {
			if (this.isLeagueMatch && this.status !== EVENT_STATUS.UPCOMING) {
				return this.leagueName;
			}
			if ((this.eventType === EVENT_TYPE.MATCH_OTHER && this.status !== EVENT_STATUS.UPCOMING)) {
				return this.categoryChild;
			}
			if ((this.isMatch && this.status !== EVENT_STATUS.UPCOMING)) {
				return null;
			}
			return this.eventName;
		},
		isTeamTraining() {
			return this.eventType === EVENT_TYPE.TEAM_TRAINING;
		},
		infoSubTitle() {
			if ((this.eventType === EVENT_TYPE.MATCH_OTHER && this.status !== EVENT_STATUS.UPCOMING)) {
				return this.location;
			}
			if (this.tags && this.tags.some(tag => tag.type === 'highlight')) {
				return this.categoryChild;
			}
			if (this.eventType === EVENT_TYPE.SINGLE) {
				return this.categoryChild;
			}
			if (this.eventType === EVENT_TYPE.SINGLE_TRAINING) {
				return `${this.categoryParent} · ${this.categoryChild}`;
			}
			if (this.isTeamTraining) {
				return this.categoryParent;
			}
			if (this.isLeagueMatch && this.location) {
				return this.location;
			}

			return this.categoryChild;
		},
		eventType() {
			return getEventType(this.event);
		},
		eventName() {
			return getFormattedTitle(this.event, { noLineBreaksForTeamNames: true });
		},
		isLeagueMatch() {
			return isLeagueMatch(this.event);
		},
		tags() {
			return getTags(this.event);
		},
		isMatch() {
			return this.eventType === EVENT_TYPE.MATCH_LEAGUE || this.eventType === EVENT_TYPE.MATCH_OTHER;
		},
		categoryParent() {
			return getCategoryParent(this.event);
		},
		categoryChild() {
			return getCategoryChild(this.event);
		},
	},
	methods: {
		handlePlay() {
			if (!this.event.isActive) {
				this.$emit('play', this.event);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.matchday--item {
	box-sizing: border-box;
	width: 100%;
	position: relative;
	padding-top: 24px;
	padding-left: 8px;
	padding-right: 8px;
	margin-top: -1px;

	.is-not-match {
		flex-direction: column;

		.event-name {
			margin-top: 2px;
			margin-bottom: 4px;
			@include text-md-1;
		}
	}

	&.is-active {
		background: var(--color-grey-900);
	}

	.event-league-image {
		height: 16px;
		margin-right: 8px;
	}

	@media(hover:hover) {
		&:not(.is-active):hover {
			background: var(--color-grey-950);

			body.-t-krone & {
				background: var(--color-grey-900);
			}
		}
	}

	&:not(.is-active) {
		cursor: pointer;
	}

	.matchday--status {
		display: flex;
		@include text-sm-1;
		margin-top: 0.5em;
		margin-bottom: 24px;
		color: var(--color-grey-200);
	}

	.matchday--item-infotext {
		@include text-sm-1;
		color: var(--color-grey-300);
		margin-bottom: $sp4;
	}

	.matchday--item-stats {
		display: grid;
		gap: $sp3-5 $sp3;
		margin-bottom: $sp4;

		&.-repeat-0 {
			grid-template-columns: 24px 70px 24px;
		}

		&.-repeat-1 {
			grid-template-columns: 24px 70px 24px 22px;
		}

		&.-repeat-2 {
			grid-template-columns: 24px 70px 24px repeat(2, 22px);
		}

		&.-repeat-3 {
			grid-template-columns: 24px 70px 24px repeat(3, 22px);
		}

		&.-repeat-4 {
			grid-template-columns: 24px 70px 24px repeat(4, 22px);
		}

		&.-repeat-5 {
			grid-template-columns: 24px 70px 24px repeat(5, 22px);
		}

		.team-logo img {
			height: 24px;
		}

		.team-name {
			@include text-sm-2;
			justify-content: flex-start;
			margin-right: $sp3;

			@include size(md-up) {
				margin-right: $sp4;
			}
		}

		.score {
			@include text-sm-2(true);
		}

		.set {
			@include text-sm-2;

			&.-loser {
				color: var(--color-grey-300);
			}
		}

		// first set
		.score + .set {
			margin-left: $sp3;
		}

	}

	.matchday--item-stats-item {
		display: flex;
		align-items: center;
		justify-content: center;

		&.-with-extra {
			position: relative;

			&::after {
				@include font(primary, book, normal, fs-60);
				line-height: inherit;
				content: attr(data-extra);
				position: absolute;
				right: -18px;
				color: var(--color-grey-300);
			}
		}
	}

	.matchday--item-info {
		@include text-sm-1;
		color: var(--color-grey-300);

		ul {
			list-style-type: none;

			li {
				margin: 0 2px;
			}
		}
	}
}

@keyframes blinking {
	0% { opacity: 1; }
	80% { opacity: 0; }
	100% { opacity: 0; }
}

.divider {
	height: 1px;
	margin-right: 8px;
	background: var(--color-grey-900);
}
</style>
